import { Fragment, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import Dropdown from '@/components/Dropdown';
import { useGetLoanPaymentSchedule } from '@/query/queries/loan/useGetLoanPaymentSchedule';
import { PAYMENT_STATUS_LABELS } from '@/helpers/translation';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { useModal } from '@/providers/modal';
import { prettyDate } from '@/helpers/date';
import { prettyFloatMoney } from '@/helpers/utils';
import { setModals } from '@/store/actions/commonActions';
import { MyTooltip } from '@/webkit/MyTooltip';
import IconCalendar from './icon_calendar.svg';
import IconChevron from './icon_chevron_up.svg';
import s from './index.module.scss';

export const PaymentSchedule = ({ fundraisingId, loan }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('loans');
  const { openSingleModal, setData } = useModal();
  const { data } = useGetLoanPaymentSchedule(fundraisingId);
  const [show, setShow] = useState(null);

  const onShiftClick = selected => {
    const payments = data.list.reduce((acc, item) => {
      if (!item.payments.length) {
        item.can_reschedule &&
          acc.push({
            deadline: item.deadline,
            status: item.status,
            description: '',
            amount: item.total_amount,
            original_deadline: item.original_deadline,
            selected: selected.original_deadline === item.original_deadline,
          });
        return acc;
      }
      item.payments.map(
        p =>
          p.can_reschedule &&
          acc.push({
            status: null,
            deadline: item.deadline,
            description: p.description || '',
            amount: p.amount,
            original_deadline: p.original_deadline,
            selected: selected.original_deadline === p.original_deadline,
          }),
      );
      return acc;
    }, []);
    setData(() => ({ payments, feeAmount: data.reschedule_fee_amount, fundraisingId, loan }));
    openSingleModal(MODAL_IDS.shift_payment_modal);
    dispatch(setModals([]));
  };

  if (!data) return null;

  return (
    <div className={s.wrapper}>
      <Dropdown
        labelFunc={show => (
          <div className={s.header}>
            <h2 className="f-22 f-500">{t('payment_schedule.payment_schedule')}</h2>
            {data.reschedule_fee_amount && (
              <div className={s.flexibility}>
                {!show && (
                  <>
                    <span className={s.flexibility_tooltip}>
                      {t('payment_schedule.shifts_are_available')}
                    </span>
                    <span className={s.divider} />
                  </>
                )}
                <span>
                  {t('payment_schedule.fee_per_shift', {
                    fee: prettyFloatMoney(data.reschedule_fee_amount),
                  })}
                </span>
              </div>
            )}
          </div>
        )}
      >
        <div className={s.table}>
          <div className={s.thead}>
            <div className={s.thead_col}>{t('payment_schedule.due_date')}</div>
            <div className={s.thead_col}>{t('payment_schedule.amount')}</div>
            <div className={s.thead_col}>{t('payment_schedule.status')}</div>
            <div className={s.thead_col} />
          </div>
          <div className={s.tbody}>
            {data.list.map(payment => (
              <Fragment key={payment.deadline}>
                <div className={s.tbody_row}>
                  <div className={s.tbody_col}>{prettyDate(payment.deadline)}</div>
                  <div className={s.tbody_col}>{prettyFloatMoney(payment.total_amount)}</div>
                  <div className={s.tbody_col}>
                    <span className={classnames(s.badge, payment.status)}>
                      {PAYMENT_STATUS_LABELS(t)[payment.status]}
                    </span>
                  </div>
                  <div className={s.tbody_col}>
                    {payment.payments.length ? (
                      <button
                        type="type"
                        onClick={() => setShow(show === payment.deadline ? null : payment.deadline)}
                        className={classnames(
                          s.chevron,
                          show === payment.deadline && s.chevron_down,
                        )}
                      >
                        <img src={IconChevron} alt="chevron" width={20} height={20} />
                      </button>
                    ) : (
                      payment.can_reschedule && (
                        <MyTooltip
                          theme="dark"
                          renderContent={() => (
                            <div className={s.calendar_tooltip}>
                              {t('payment_schedule.move_to_next_month')}
                            </div>
                          )}
                          renderElement={() => (
                            <button
                              type="button"
                              className={s.calendar}
                              onClick={() => onShiftClick(payment)}
                            >
                              <img src={IconCalendar} alt="calendar" width={20} height={20} />
                            </button>
                          )}
                        />
                      )
                    )}
                  </div>
                </div>

                {payment.deadline === show &&
                  payment.payments.map(subpayment => (
                    <div className={s.tbody_row} key={subpayment.original_deadline}>
                      <div className={s.tbody_col} />
                      <div className={s.tbody_col}>{prettyFloatMoney(subpayment.amount)}</div>
                      <div className={s.tbody_col}>
                        <span className={s.flexibility_desc}>
                          {subpayment.description || t('payment_schedule.as_scheduled')}
                        </span>
                      </div>
                      <div className={s.tbody_col}>
                        {subpayment.can_reschedule && (
                          <MyTooltip
                            theme="dark"
                            renderContent={() => (
                              <div className={s.calendar_tooltip}>
                                {t('payment_schedule.move_to_next_month')}
                              </div>
                            )}
                            renderElement={() => (
                              <button
                                type="button"
                                className={s.calendar}
                                onClick={() => onShiftClick(subpayment)}
                              >
                                <img src={IconCalendar} alt="calendar" width={20} height={20} />
                              </button>
                            )}
                          />
                        )}
                      </div>
                    </div>
                  ))}
              </Fragment>
            ))}
          </div>
        </div>
      </Dropdown>
    </div>
  );
};
